import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useClientV2} from "../../../../utils/hooks";
import {AxiosError} from "axios";
import {message} from "antd";
import {GET_STORE_SUBSCRIPTION_RQ_KEY} from "../constants";
import {invalidateSubscriptionListQueries} from "../invalidate-subscription-lists";
import {
  CreateSubscriptionApiResponse,
  CreateSubscriptionApiPayload,
  CreateSubscriptionApiRawResponse,
} from "./types";
import {parseTiers} from "../get-subscription/normalize";

function normalizeApiResponse(
  raw: CreateSubscriptionApiRawResponse
): CreateSubscriptionApiResponse {
  const {data} = raw;
  const o = data as unknown as Record<string, unknown>;
  const slug =
    (data.slug && data.slug.trim()) ||
    data.name?.trim().toLowerCase().replace(/\s+/g, "-") ||
    "";
  const tinySource = o.tinyUrl ?? o.tinyurl ?? o.tiny_url;
  const tiny_url =
    typeof tinySource === "string" && tinySource.trim() !== ""
      ? tinySource.trim()
      : undefined;

  return {
    productSubscription: {
      id: data.id,
      name: data.name,
      slug,
      description: data.description ?? "",
      cover_images: [],
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      ...(tiny_url !== undefined ? {tiny_url} : {}),
      tiers: parseTiers(data.tiers, data.id),
    },
  };
}

export function formatCreateSubscriptionError(error: unknown) {
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    if (typeof record.message === "string") {
      return record.message;
    }
    if (Array.isArray(record.message)) {
      return record.message.filter((item) => typeof item === "string").join(", ");
    }
    if (typeof record.error === "string") {
      return record.error;
    }
  }
  return "Could not create subscription. Please try again.";
}

export const useCreateSubscriptionApi = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation<
    CreateSubscriptionApiResponse,
    AxiosError<{message: string}, unknown>,
    CreateSubscriptionApiPayload,
    unknown
  >({
    mutationFn: async (payload: CreateSubscriptionApiPayload) => {
      const raw = await client(`subscriptions`, {
        method: "POST",
        data: payload,
      });
      return normalizeApiResponse(raw as CreateSubscriptionApiRawResponse);
    },
    onError: (error: unknown) => {
      message.error(formatCreateSubscriptionError(error));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_RQ_KEY],
      });
      invalidateSubscriptionListQueries(queryClient);
    },
  });
};
